/**
 * Centralized retry heuristics for the NATIVE printing strategy.
 *
 * Ported from cartadigital-pos (app/utils/printing/retry-policy.ts, constants
 * from app/stores/print-queue.ts). This module intentionally relies on string
 * matching because errors come from transports and native plugins that don't
 * always provide structured codes. Keep the surface small and reuse everywhere
 * to avoid drift.
 *
 * IMPORTANT: this classification only drives the bounded in-strategy retry
 * loop. The `retrySafety` that marking policies act on is derived from the
 * FAILURE PHASE (pre-send vs send) in the native strategy, never from here.
 */

/** Max automatic in-strategy retries per job (native strategy only). */
export const NATIVE_PRINT_MAX_RETRIES = 3;

/** Delay before retry attempt N (1-based). */
export const NATIVE_PRINT_RETRY_DELAYS_MS = [1000, 3000, 7000];

export const getLowerErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error);
  return message.toLowerCase();
};

export const isNonIdempotentUsbSendErrorMessage = (
  lowerMessage: string
): boolean => {
  // If we can't know whether bytes were partially printed, we must avoid retries.
  return (
    lowerMessage.includes('bulk transfer') ||
    lowerMessage.includes('error al enviar datos a la impresora usb') ||
    (lowerMessage.includes('impresora usb') && lowerMessage.includes('enviar'))
  );
};

export const isSafeToAutoRetryMessage = (lowerMessage: string): boolean => {
  if (isNonIdempotentUsbSendErrorMessage(lowerMessage)) return false;

  // Safe-ish buckets: failures before sending bytes, or before resolving mapping.
  return (
    lowerMessage.includes('impresora física') ||
    lowerMessage.includes('error de conexión con la impresora') ||
    lowerMessage.includes('error de conexión') ||
    lowerMessage.includes('error de conexion con la impresora') ||
    lowerMessage.includes('error de conexion') ||
    lowerMessage.includes('intente de nuevo') ||
    lowerMessage.includes('error creating printer handle') ||
    lowerMessage.includes('error connecting to printer') ||
    lowerMessage.includes('connection refused') ||
    lowerMessage.includes('connection reset') ||
    lowerMessage.includes('connection lost') ||
    lowerMessage.includes('socket') ||
    lowerMessage.includes('network') ||
    lowerMessage.includes('econnrefused') ||
    lowerMessage.includes('econnreset') ||
    lowerMessage.includes('etimedout') ||
    lowerMessage.includes('timeout') ||
    lowerMessage.includes('tiempo de conexión agotado') ||
    lowerMessage.includes('tiempo de conexion agotado')
  );
};

export const isNonIdempotentUsbSendError = (error: unknown): boolean => {
  return isNonIdempotentUsbSendErrorMessage(getLowerErrorMessage(error));
};

export const isSafeToAutoRetryError = (error: unknown): boolean => {
  return isSafeToAutoRetryMessage(getLowerErrorMessage(error));
};

export type IAutoRetryReason =
  | 'non_idempotent'
  | 'unsafe'
  | 'safe'
  | 'max_retries_exceeded';

export interface IAutoRetryDecision {
  shouldRetry: boolean;
  isNonIdempotent: boolean;
  reason: IAutoRetryReason;
}

export const getAutoRetryDecision = (params: {
  error: unknown;
  attempts: number;
  maxRetries: number;
}): IAutoRetryDecision => {
  const attempts =
    Number.isFinite(params.attempts) && params.attempts >= 0
      ? Math.floor(params.attempts)
      : 0;
  const maxRetries =
    Number.isFinite(params.maxRetries) && params.maxRetries >= 0
      ? Math.floor(params.maxRetries)
      : 0;

  if (isNonIdempotentUsbSendError(params.error)) {
    return {
      shouldRetry: false,
      isNonIdempotent: true,
      reason: 'non_idempotent',
    };
  }

  if (!isSafeToAutoRetryError(params.error)) {
    return {
      shouldRetry: false,
      isNonIdempotent: false,
      reason: 'unsafe',
    };
  }

  if (attempts >= maxRetries) {
    return {
      shouldRetry: false,
      isNonIdempotent: false,
      reason: 'max_retries_exceeded',
    };
  }

  return {
    shouldRetry: true,
    isNonIdempotent: false,
    reason: 'safe',
  };
};
